import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * A single filter preset configuration
 */
export interface FilterPreset {
  /** Unique identifier for the preset */
  id: string;
  /** User-defined name for the preset */
  name: string;
  /** Location ID to filter by */
  locationId: string;
  /** Optional: Location name for display purposes */
  locationName?: string;
  /** Device ID to filter by */
  deviceId: string;
  /** Optional: Device name for display purposes */
  deviceName?: string;
  /** Time interval for aggregation, or 'realtime' for raw data points */
  interval: 'realtime' | 'minute' | 'hour' | 'day' | 'week' | 'month';
  /** Timezone for displaying chart data */
  timezone: string;
  /** Number of days to look back for date range */
  dateRangeDays: number;
  /** Tab to show (timeseries, gauges, devices, ai-insights) */
  tab: string;
  /** When the preset was created */
  createdAt: string;
  /** When the preset was last updated */
  updatedAt: string;
}

/**
 * Filter values used to create/update a preset
 */
export interface FilterValues {
  locationId: string;
  locationName?: string;
  deviceId: string;
  deviceName?: string;
  interval: FilterPreset['interval'];
  timezone: string;
  dateRangeDays: number;
  tab?: string;
}

interface AnalyticsPreferencesStore {
  /** All saved presets */
  presets: FilterPreset[];
  
  /** ID of the currently active preset (null if using system defaults) */
  activePresetId: string | null;
  
  /** Create a new preset with the given name and filter values */
  createPreset: (name: string, filters: FilterValues) => FilterPreset;
  
  /** Update an existing preset */
  updatePreset: (id: string, updates: Partial<Omit<FilterPreset, 'id' | 'createdAt'>>) => void;
  
  /** Delete a preset by ID */
  deletePreset: (id: string) => void;
  
  /** Rename a preset */
  renamePreset: (id: string, newName: string) => void;
  
  /** Set the active preset */
  setActivePreset: (id: string | null) => void;
  
  /** Get a preset by ID */
  getPreset: (id: string) => FilterPreset | undefined;
  
  /** Get the active preset (or null if none) */
  getActivePreset: () => FilterPreset | null;
  
  /** Check if any presets exist */
  hasPresets: () => boolean;
  
  /** Duplicate a preset */
  duplicatePreset: (id: string) => FilterPreset | null;
}

const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36);

/** System default filter values */
export const SYSTEM_DEFAULTS: Omit<FilterValues, 'locationName' | 'deviceName'> = {
  locationId: '',
  deviceId: '',
  interval: 'hour',
  timezone: 'local',
  dateRangeDays: 7,
  tab: 'timeseries',
};

export const useAnalyticsPreferencesStore = create<AnalyticsPreferencesStore>()(
  persist(
    (set, get) => ({
      presets: [],
      activePresetId: null,

      createPreset: (name, filters) => {
        const now = new Date().toISOString();
        const newPreset: FilterPreset = {
          id: generateId(),
          name,
          locationId: filters.locationId,
          locationName: filters.locationName,
          deviceId: filters.deviceId,
          deviceName: filters.deviceName,
          interval: filters.interval,
          timezone: filters.timezone,
          dateRangeDays: filters.dateRangeDays,
          tab: filters.tab || 'timeseries',
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          presets: [...state.presets, newPreset],
          activePresetId: newPreset.id,
        }));

        return newPreset;
      },

      updatePreset: (id, updates) => {
        set((state) => ({
          presets: state.presets.map((preset) =>
            preset.id === id
              ? { ...preset, ...updates, updatedAt: new Date().toISOString() }
              : preset
          ),
        }));
      },

      deletePreset: (id) => {
        set((state) => ({
          presets: state.presets.filter((preset) => preset.id !== id),
          activePresetId: state.activePresetId === id ? null : state.activePresetId,
        }));
      },

      renamePreset: (id, newName) => {
        set((state) => ({
          presets: state.presets.map((preset) =>
            preset.id === id
              ? { ...preset, name: newName, updatedAt: new Date().toISOString() }
              : preset
          ),
        }));
      },

      setActivePreset: (id) => {
        set({ activePresetId: id });
      },

      getPreset: (id) => {
        return get().presets.find((preset) => preset.id === id);
      },

      getActivePreset: () => {
        const { presets, activePresetId } = get();
        if (!activePresetId) return null;
        return presets.find((preset) => preset.id === activePresetId) || null;
      },

      hasPresets: () => {
        return get().presets.length > 0;
      },

      duplicatePreset: (id) => {
        const preset = get().getPreset(id);
        if (!preset) return null;

        const now = new Date().toISOString();
        const duplicatedPreset: FilterPreset = {
          ...preset,
          id: generateId(),
          name: `${preset.name} (Copy)`,
          createdAt: now,
          updatedAt: now,
        };

        set((state) => ({
          presets: [...state.presets, duplicatedPreset],
        }));

        return duplicatedPreset;
      },
    }),
    {
      name: 'analytics-preferences',
      partialize: (state) => ({
        presets: state.presets,
        activePresetId: state.activePresetId,
      }),
    },
  ),
);

/**
 * Hook to get presets and active preset
 */
export const useAnalyticsPresets = () => {
  const presets = useAnalyticsPreferencesStore((state) => state.presets);
  const activePresetId = useAnalyticsPreferencesStore((state) => state.activePresetId);
  const activePreset = presets.find((p) => p.id === activePresetId) || null;
  
  return { presets, activePresetId, activePreset };
};
